import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useParams } from "react-router-dom";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import {
  createBaasPhysicalCard,
  fetchBaasCardAccountById,
  fetchBaasPhysicalCards,
  linkBaasCardAccount,
  patchBaasCardAccountCredit,
  unlinkBaasCardAccount,
} from "../../../../Services/baasCardAccountsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { formatInputValue } from "../../../../Utils/formatInputValue";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import {
  formatBrlMajorMask,
  parseBrlMajorMask,
} from "../Instances/baasInstanceCreateConstants";
import { BaasCardProgramDetailSkeleton } from "../components/BaasSkeleton";
import { BAAS_CARTOES_ACCOUNTS_PATH } from "../constants";

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-3 last:border-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <span className="break-all text-sm font-medium text-brand-dark dark:text-gray-100">
      {value ?? "—"}
    </span>
  </div>
);

/** Major-unit money object or raw amount — pt-BR R$ display. */
const formatMoney = (money) => {
  if (money == null || money === "") return "—";
  if (typeof money === "object") {
    if (money.amount == null || money.amount === "") return "—";
    return formatMonetaryValue(money.amount, money.currency || "BRL");
  }
  return formatMonetaryValue(money, "BRL");
};

const toMaskedMajor = (amount) => {
  if (amount == null || amount === "") return "";
  return formatBrlMajorMask(amount);
};

const formatAddress = (address) => {
  if (!address) return "—";
  const parts = [
    address.address_line1,
    address.address_line2,
    address.neighborhood,
    address.city,
    address.state,
    address.zipcode,
    address.country_code,
  ].filter(Boolean);
  return parts.join(", ") || "—";
};

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-brand-dark outline-none focus:border-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100 dark:focus:border-gray-500";

const emptyIssueForm = () => ({
  embossingName: "",
  fullName: "",
  identityDocumentNumber: "",
  birthDate: "",
  nationality: "Brasil",
  deliveryKitCode: "000021",
  inhibitPhysicalCard: false,
  addressLine1: "",
  addressLine2: "",
  neighborhood: "",
  city: "",
  state: "",
  zipcode: "",
  countryCode: "BR",
});

export const BaasCardAccountDetailPage = () => {
  const { issuerAccountId } = useParams();
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can } = useBaasCapabilities();
  const canWrite = can("issuer.programs.create");

  const [account, setAccount] = useState(null);
  const [cards, setCards] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [linkAccountId, setLinkAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(emptyIssueForm);
  const [creditForm, setCreditForm] = useState({
    creditLimitAmount: "",
    withdrawalLimitAmount: "",
    paymentDue: "",
  });

  const loadCards = useCallback(
    async (acct) => {
      if (!selectedInstance || !acct?.id) {
        setCards([]);
        return;
      }
      try {
        const list = await fetchBaasPhysicalCards(selectedInstance, acct.id);
        setCards(list);
      } catch (error) {
        setCards([]);
        addToast({
          type: "error",
          title: formatBaasConsoleConflictMessage(
            error,
            intl.get("baas.cartoes.accounts.detail.cardsLoadError"),
          ),
        });
      }
    },
    [addToast, selectedInstance],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance || !issuerAccountId) {
        setAccount(null);
        setCards([]);
        return;
      }

      setDataLoading(true);
      setNotFound(false);
      try {
        const next = await fetchBaasCardAccountById(selectedInstance, issuerAccountId);
        if (!cancelled) {
          setAccount(next);
          setLinkAccountId(next?.accountId || "");
          setCreditForm({
            creditLimitAmount: toMaskedMajor(next?.creditInfo?.creditLimit?.amount),
            withdrawalLimitAmount: toMaskedMajor(next?.creditInfo?.withdrawalLimit?.amount),
            paymentDue: next?.creditInfo?.paymentDue ?? "",
          });
          await loadCards(next);
        }
      } catch (error) {
        if (!cancelled) {
          setAccount(null);
          setCards([]);
          setNotFound(true);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.accounts.detail.loadError"),
            ),
          });
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, issuerAccountId, loadCards, selectedInstance]);

  if (isLoading || dataLoading) {
    return <BaasCardProgramDetailSkeleton />;
  }

  if (notFound || !account) {
    return <Redirect to={BAAS_CARTOES_ACCOUNTS_PATH} />;
  }

  const openIssueForm = () => {
    const delivery = account.cardDeliveryAddress || {};
    setIssueForm({
      ...emptyIssueForm(),
      embossingName: String(account.personName || "")
        .trim()
        .toUpperCase()
        .slice(0, 26),
      fullName: account.personName || "",
      addressLine1: delivery.address_line1 || "",
      addressLine2: delivery.address_line2 || "",
      neighborhood: delivery.neighborhood || "",
      city: delivery.city || "",
      state: delivery.state || "",
      zipcode: delivery.zipcode || "",
      countryCode: delivery.country_code || "BR",
    });
    setIssueOpen(true);
  };

  const handleLink = async () => {
    if (!linkAccountId?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.detail.linkRequired"),
      });
      return;
    }

    setBusy(true);
    try {
      const updated = await linkBaasCardAccount(
        selectedInstance,
        account.id,
        linkAccountId.trim(),
      );
      setAccount(updated);
      setLinkAccountId(updated.accountId || "");
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.accounts.detail.linkSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.accounts.detail.linkError"),
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      const updated = await unlinkBaasCardAccount(selectedInstance, account.id);
      setAccount(updated);
      setLinkAccountId("");
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.accounts.detail.unlinkSuccess"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.accounts.detail.unlinkError"),
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleIssueCard = async () => {
    if (!issueForm.embossingName?.trim() || !issueForm.fullName?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.detail.issue.validationRequired"),
      });
      return;
    }
    if (!issueForm.identityDocumentNumber?.trim() || !issueForm.birthDate?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.detail.issue.validationRequired"),
      });
      return;
    }
    if (!issueForm.addressLine1?.trim() || !issueForm.city?.trim() || !issueForm.state?.trim() || !issueForm.zipcode?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.detail.issue.validationAddress"),
      });
      return;
    }

    setBusy(true);
    try {
      await createBaasPhysicalCard(selectedInstance, account.id, {
        embossingName: issueForm.embossingName,
        fullName: issueForm.fullName,
        identityDocumentNumber: issueForm.identityDocumentNumber,
        birthDate: issueForm.birthDate,
        nationality: issueForm.nationality,
        deliveryKitCode: issueForm.deliveryKitCode,
        inhibitPhysicalCard: issueForm.inhibitPhysicalCard,
        psProductCode: account.psProductCode,
        cardDeliveryAddress: {
          addressLine1: issueForm.addressLine1,
          addressLine2: issueForm.addressLine2,
          neighborhood: issueForm.neighborhood,
          city: issueForm.city,
          state: issueForm.state,
          zipcode: issueForm.zipcode,
          countryCode: issueForm.countryCode,
        },
      });
      setIssueOpen(false);
      await loadCards(account);
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.accounts.detail.issue.success"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.accounts.detail.issue.error"),
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateCredit = async () => {
    const creditLimitAmount = parseBrlMajorMask(creditForm.creditLimitAmount);
    if (!Number.isFinite(creditLimitAmount) || creditLimitAmount < 0) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.detail.credit.limitRequired"),
      });
      return;
    }

    let withdrawalLimitAmount = "";
    if (creditForm.withdrawalLimitAmount !== "" && creditForm.withdrawalLimitAmount != null) {
      const parsed = parseBrlMajorMask(creditForm.withdrawalLimitAmount);
      if (!Number.isFinite(parsed) || parsed < 0) {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.accounts.detail.credit.limitRequired"),
        });
        return;
      }
      withdrawalLimitAmount = parsed;
    }

    setBusy(true);
    try {
      const updated = await patchBaasCardAccountCredit(selectedInstance, account.id, {
        creditLimitAmount,
        withdrawalLimitAmount,
        paymentDue: creditForm.paymentDue,
        creditCurrencyCode: account.creditInfo?.creditLimit?.currencyCode || 986,
      });
      setAccount(updated);
      setCreditForm({
        creditLimitAmount: toMaskedMajor(updated?.creditInfo?.creditLimit?.amount),
        withdrawalLimitAmount: toMaskedMajor(updated?.creditInfo?.withdrawalLimit?.amount),
        paymentDue: updated?.creditInfo?.paymentDue ?? "",
      });
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.accounts.detail.credit.success"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.accounts.detail.credit.error"),
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const statusLabel =
    intl.get(`baas.cartoes.accounts.status.${account.status}`) || account.status;

  const setIssueField = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setIssueForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="baas-card-account-detail min-w-0 space-y-5">
      <PageHeaderWithBack
        title={account.personName || account.id}
        subTitle={intl.get("baas.cartoes.accounts.detail.subtitle")}
        backTo={BAAS_CARTOES_ACCOUNTS_PATH}
      />

      <section className="rounded-xl border border-gray-200 bg-white px-4 py-2 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label pt-3">
          {intl.get("baas.cartoes.accounts.detail.overview")}
        </p>
        <DetailRow label={intl.get("baas.cartoes.accounts.columns.id")} value={account.id} />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.status")}
          value={statusLabel}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.program")}
          value={account.cardProgramName || account.cardProgramId}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.product")}
          value={
            account.product
              ? `${account.product.name} (${account.product.code})`
              : account.psProductCode
          }
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.person")}
          value={account.personName || account.personId}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.personId")}
          value={account.personId}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.creditLimit")}
          value={formatMoney(account.creditInfo?.creditLimit)}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.create.withdrawalLimit")}
          value={formatMoney(account.creditInfo?.withdrawalLimit)}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.create.paymentDue")}
          value={account.creditInfo?.paymentDue ?? "—"}
        />
      </section>

      {account.product?.supportsCreditInfo ? (
        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <p className="dashboard-section-label">
            {intl.get("baas.cartoes.accounts.detail.credit.section")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.accounts.detail.credit.unitsHint")}
          </p>
          {account.programReserve ? (
            <div className="rounded-lg border border-gray-100 px-3 dark:border-brand-dark-border">
              <DetailRow
                label={intl.get("baas.cartoes.accounts.detail.credit.reserveAvailable")}
                value={formatMoney({
                  amount: account.programReserve.available,
                  currency: "BRL",
                })}
              />
              <DetailRow
                label={intl.get("baas.cartoes.accounts.detail.credit.reserveHeld")}
                value={formatMoney({
                  amount: account.programReserve.held,
                  currency: "BRL",
                })}
              />
              <DetailRow
                label={intl.get("baas.cartoes.accounts.detail.credit.guaranteePercent")}
                value={`${account.programReserve.guaranteePercent}%`}
              />
              <DetailRow
                label={intl.get("baas.cartoes.accounts.detail.credit.accountHeld")}
                value={formatMoney({
                  amount: account.creditReserveHeld,
                  currency: "BRL",
                })}
              />
            </div>
          ) : null}
          {canWrite ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.creditLimit")}
                </span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    R$
                  </span>
                  <input
                    className={`${inputClass} pl-9`}
                    type="text"
                    inputMode="numeric"
                    value={creditForm.creditLimitAmount}
                    onChange={(e) =>
                      setCreditForm((prev) => ({
                        ...prev,
                        creditLimitAmount: formatInputValue(String(e.target.value ?? "")),
                      }))
                    }
                    disabled={busy}
                  />
                </div>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.withdrawalLimit")}
                </span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    R$
                  </span>
                  <input
                    className={`${inputClass} pl-9`}
                    type="text"
                    inputMode="numeric"
                    value={creditForm.withdrawalLimitAmount}
                    onChange={(e) =>
                      setCreditForm((prev) => ({
                        ...prev,
                        withdrawalLimitAmount: formatInputValue(String(e.target.value ?? "")),
                      }))
                    }
                    disabled={busy}
                  />
                </div>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.paymentDue")}
                </span>
                <input
                  className={inputClass}
                  type="number"
                  min="1"
                  max="31"
                  value={creditForm.paymentDue}
                  onChange={(e) =>
                    setCreditForm((prev) => ({ ...prev, paymentDue: e.target.value }))
                  }
                  disabled={busy}
                />
              </label>
              <div className="sm:col-span-3">
                <button
                  type="button"
                  className="btn-primary !text-xs"
                  onClick={handleUpdateCredit}
                  disabled={busy}
                >
                  {intl.get("baas.cartoes.accounts.detail.credit.update")}
                </button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="rounded-xl border border-gray-200 bg-white px-4 py-2 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label pt-3">
          {intl.get("baas.cartoes.accounts.detail.addresses")}
        </p>
        <DetailRow
          label={intl.get("baas.cartoes.accounts.create.sections.billing")}
          value={formatAddress(account.billingAddress)}
        />
        <DetailRow
          label={intl.get("baas.cartoes.accounts.create.sections.delivery")}
          value={formatAddress(account.cardDeliveryAddress)}
        />
      </section>

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="dashboard-section-label !mb-0">
            {intl.get("baas.cartoes.accounts.detail.cards")}
          </p>
          {canWrite ? (
            <button
              type="button"
              className="btn-primary !text-xs"
              onClick={openIssueForm}
              disabled={busy}
            >
              {intl.get("baas.cartoes.accounts.detail.issue.button")}
            </button>
          ) : null}
        </div>

        {cards.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.accounts.detail.cardsEmpty")}
          </p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-brand-dark-border">
            {cards.map((card) => (
              <div key={card.id} className="py-3">
                <DetailRow
                  label={intl.get("baas.cartoes.accounts.detail.issue.columns.id")}
                  value={card.id}
                />
                <DetailRow
                  label={intl.get("baas.cartoes.accounts.columns.status")}
                  value={
                    intl.get(`baas.cartoes.accounts.detail.issue.status.${card.status}`) ||
                    card.status
                  }
                />
                <DetailRow
                  label={intl.get("baas.cartoes.accounts.detail.issue.columns.embossing")}
                  value={card.embossingName}
                />
                <DetailRow
                  label={intl.get("baas.cartoes.accounts.detail.issue.columns.kit")}
                  value={card.deliveryKitCode || "—"}
                />
                {card.statusNote ? (
                  <DetailRow
                    label={intl.get("baas.cartoes.accounts.detail.issue.columns.note")}
                    value={card.statusNote}
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}

        {issueOpen ? (
          <div className="mt-3 space-y-3 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.cartoes.accounts.detail.issue.title")}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.detail.issue.embossingName")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.embossingName}
                  onChange={setIssueField("embossingName")}
                  disabled={busy}
                  maxLength={26}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.fullName")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.fullName}
                  onChange={setIssueField("fullName")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.document")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.identityDocumentNumber}
                  onChange={setIssueField("identityDocumentNumber")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.detail.issue.birthDate")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.birthDate}
                  onChange={setIssueField("birthDate")}
                  placeholder="dd/MM/yyyy"
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.detail.issue.deliveryKitCode")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.deliveryKitCode}
                  onChange={setIssueField("deliveryKitCode")}
                  disabled={busy}
                  maxLength={6}
                />
              </label>
              <label className="flex items-center gap-2 pt-6 text-sm text-brand-dark dark:text-gray-100">
                <input
                  type="checkbox"
                  checked={issueForm.inhibitPhysicalCard}
                  onChange={setIssueField("inhibitPhysicalCard")}
                  disabled={busy}
                />
                {intl.get("baas.cartoes.accounts.detail.issue.inhibit")}
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5 sm:col-span-2">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.addressLine1")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.addressLine1}
                  onChange={setIssueField("addressLine1")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.addressLine2")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.addressLine2}
                  onChange={setIssueField("addressLine2")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.neighborhood")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.neighborhood}
                  onChange={setIssueField("neighborhood")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.city")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.city}
                  onChange={setIssueField("city")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.state")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.state}
                  onChange={setIssueField("state")}
                  disabled={busy}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {intl.get("baas.cartoes.accounts.create.zipcode")}
                </span>
                <input
                  className={inputClass}
                  value={issueForm.zipcode}
                  onChange={setIssueField("zipcode")}
                  disabled={busy}
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary !text-xs"
                onClick={handleIssueCard}
                disabled={busy}
              >
                {busy
                  ? intl.get("baas.cartoes.accounts.detail.issue.submitting")
                  : intl.get("baas.cartoes.accounts.detail.issue.submit")}
              </button>
              <button
                type="button"
                className="btn-outline !text-xs"
                onClick={() => setIssueOpen(false)}
                disabled={busy}
              >
                {intl.get("baas.cartoes.accounts.create.cancel")}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label">
          {intl.get("baas.cartoes.accounts.detail.bankingLink")}
        </p>
        <DetailRow
          label={intl.get("baas.cartoes.accounts.columns.linkedAccount")}
          value={account.accountId || intl.get("baas.cartoes.accounts.notLinked")}
        />
        {canWrite ? (
          <div className="flex flex-col gap-3 border-t border-gray-100 pt-3 dark:border-brand-dark-border sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1 space-y-1.5">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {intl.get("baas.cartoes.accounts.create.accountId")}
              </span>
              <input
                className={inputClass}
                value={linkAccountId}
                onChange={(e) => setLinkAccountId(e.target.value)}
                placeholder="acct_…"
                disabled={busy}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary !text-xs"
                onClick={handleLink}
                disabled={busy}
              >
                {intl.get("baas.cartoes.accounts.detail.link")}
              </button>
              {account.accountId ? (
                <button
                  type="button"
                  className="btn-outline !text-xs"
                  onClick={handleUnlink}
                  disabled={busy}
                >
                  {intl.get("baas.cartoes.accounts.detail.unlink")}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};
